'use client';
//
// App-wide icon shim. Every icon is a Phosphor glyph, re-exported under the
// lucide-react name it replaced so the ~430 existing call sites keep working
// unchanged.
//
// Phosphor icons accept the same props the codebase already passes:
//   - className="w-4 h-4"  sizes via CSS (overrides the svg size attribute)
//   - text-* / currentColor  controls color
//   - weight="fill"  selects the solid variant (used by the nav active state)
// We default size to 24 (lucide's default) for the rare call site that passes
// no width/height class; an explicit className size still wins via CSS.
//
import { forwardRef, createElement } from 'react';
import * as Ph from '@phosphor-icons/react';

// lucide rendered a "filled" state by setting the SVG fill (e.g. a liked Heart
// got `fill-current` / `fill-red-500`, or a `fill="currentColor"` prop). Phosphor
// instead swaps to a solid glyph via weight="fill". So when a call site asks for
// a fill the lucide way, translate it to weight="fill" automatically — that keeps
// every "filled when active" toggle (like, save, star, …) working untouched.
// We only trigger on a *base* fill utility (not `group-hover:fill-*`, and not
// `fill-none`/`fill-transparent`, which are the inactive/outline states).
const BASE_FILL_RE = /(?:^|\s)fill-(?!none\b|transparent\b)[a-z0-9[\]/.#-]+/i;

const wrap = (Comp) => {
  const C = forwardRef(({ size, weight, className, fill, ...rest }, ref) => {
    const fillFromClass = typeof className === 'string' && BASE_FILL_RE.test(className);
    const fillFromProp = !!fill && fill !== 'none' && fill !== 'transparent';
    const resolvedWeight = weight ?? (fillFromClass || fillFromProp ? 'fill' : 'regular');
    return createElement(Comp, {
      ref,
      size: size ?? 24,
      weight: resolvedWeight,
      className,
      // Re-apply a real fill color prop (drops fill="none"); Phosphor's solid
      // path then takes this color.
      ...(fillFromProp ? { fill } : {}),
      ...rest,
    });
  });
  C.displayName = Comp.displayName || 'Icon';
  return C;
};

const P = {
  Activity: Ph.Pulse,
  AlertCircle: Ph.WarningCircle,
  AlertTriangle: Ph.Warning,
  AlignJustify: Ph.TextAlignJustify,
  AlignLeft: Ph.AlignLeft,
  AlignCenter: Ph.TextAlignCenter,
  AlignRight: Ph.TextAlignRight,
  Archive: Ph.Archive,
  ArrowBigDown: Ph.ArrowFatDown,
  ArrowBigUp: Ph.ArrowFatUp,
  ArrowDown: Ph.ArrowDown,
  ArrowLeft: Ph.ArrowLeft,
  ArrowLeftRight: Ph.ArrowsLeftRight,
  ArrowRight: Ph.ArrowRight,
  ArrowUp: Ph.ArrowUp,
  ArrowUpDown: Ph.ArrowsDownUp,
  ArrowUpRight: Ph.ArrowUpRight,
  AtSign: Ph.At,
  Award: Ph.Medal,
  BadgeCheck: Ph.SealCheck,
  Ban: Ph.Prohibit,
  BarChart2: Ph.ChartBar,
  BarChart3: Ph.ChartBar,
  Bell: Ph.Bell,
  BellOff: Ph.BellSlash,
  BookOpen: Ph.BookOpen,
  Bookmark: Ph.BookmarkSimple,
  BookmarkCheck: Ph.BookmarkSimple,
  BookmarkPlus: Ph.BookmarkSimple,
  Building2: Ph.Buildings,
  Calendar: Ph.Calendar,
  CalendarCheck: Ph.CalendarCheck,
  CalendarClock: Ph.CalendarDots,
  CalendarDays: Ph.CalendarDots,
  Camera: Ph.Camera,
  Captions: Ph.ClosedCaptioning,
  CaptionsOff: Ph.ClosedCaptioning,
  Car: Ph.Car,
  Check: Ph.Check,
  CheckCheck: Ph.Checks,
  CheckCircle: Ph.CheckCircle,
  CheckCircle2: Ph.CheckCircle,
  CheckSquare: Ph.CheckSquare,
  ChevronDown: Ph.CaretDown,
  ChevronLeft: Ph.CaretLeft,
  ChevronRight: Ph.CaretRight,
  ChevronUp: Ph.CaretUp,
  Circle: Ph.Circle,
  CircleDot: Ph.RadioButton,
  ClipboardList: Ph.ClipboardText,
  Clock: Ph.Clock,
  Clock4: Ph.Clock,
  Cloud: Ph.Cloud,
  Code: Ph.Code,
  Code2: Ph.Code,
  Coffee: Ph.Coffee,
  Compass: Ph.Compass,
  Contrast: Ph.CircleHalf,
  Copy: Ph.Copy,
  CornerDownRight: Ph.ArrowElbowDownRight,
  CreditCard: Ph.CreditCard,
  Crop: Ph.Crop,
  Crosshair: Ph.Crosshair,
  Crown: Ph.Crown,
  DollarSign: Ph.CurrencyDollar,
  Download: Ph.Download,
  Droplet: Ph.Drop,
  Dumbbell: Ph.Barbell,
  Edit: Ph.PencilSimple,
  Edit2: Ph.Pencil,
  ExternalLink: Ph.ArrowSquareOut,
  Eye: Ph.Eye,
  EyeOff: Ph.EyeSlash,
  FileCheck: Ph.FileText,
  FileEdit: Ph.NotePencil,
  FileSpreadsheet: Ph.FileXls,
  FileText: Ph.FileText,
  FileVideo: Ph.FileVideo,
  Film: Ph.FilmStrip,
  Flag: Ph.Flag,
  Flame: Ph.Flame,
  Folder: Ph.Folder,
  FolderInput: Ph.FolderOpen,
  FolderOpen: Ph.FolderOpen,
  Gauge: Ph.Gauge,
  Gem: Ph.Diamond,
  Gift: Ph.Gift,
  GitCompare: Ph.GitDiff,
  Globe: Ph.Globe,
  Globe2: Ph.Globe,
  Grid3X3: Ph.GridFour,
  Grid3x3: Ph.GridFour,
  GripVertical: Ph.DotsSixVertical,
  Handshake: Ph.Handshake,
  Hash: Ph.Hash,
  Heart: Ph.Heart,
  HelpCircle: Ph.Question,
  History: Ph.ClockCounterClockwise,
  Home: Ph.House,
  Hourglass: Ph.Hourglass,
  Image: Ph.Image,
  ImageIcon: Ph.ImageIcon,
  ImagePlus: Ph.ImageSquare,
  Images: Ph.Images,
  Info: Ph.Info,
  Keyboard: Ph.Keyboard,
  Landmark: Ph.Bank,
  Layers: Ph.Stack,
  LayoutDashboard: Ph.SquaresFour,
  LayoutGrid: Ph.GridFour,
  LayoutTemplate: Ph.Layout,
  LibraryBig: Ph.Books,
  LifeBuoy: Ph.Lifebuoy,
  Link: Ph.Link,
  Link2: Ph.LinkSimple,
  List: Ph.List,
  ListFilter: Ph.FunnelSimple,
  ListVideo: Ph.Playlist,
  Loader2: Ph.CircleNotch,
  Lock: Ph.Lock,
  LogOut: Ph.SignOut,
  Mail: Ph.Envelope,
  Map: Ph.MapTrifold,
  MapPin: Ph.MapPin,
  Maximize: Ph.ArrowsOut,
  Maximize2: Ph.ArrowsOutSimple,
  Medal: Ph.Medal,
  Megaphone: Ph.Megaphone,
  Menu: Ph.List,
  MessageCircle: Ph.ChatCircle,
  MessageSquare: Ph.Chat,
  Mic: Ph.Microphone,
  Minimize: Ph.ArrowsIn,
  Minus: Ph.Minus,
  Monitor: Ph.Monitor,
  MonitorPlay: Ph.MonitorPlay,
  Moon: Ph.Moon,
  MoreHorizontal: Ph.DotsThree,
  MoreVertical: Ph.DotsThreeVertical,
  Mountain: Ph.Mountains,
  Music2: Ph.MusicNote,
  Navigation: Ph.NavigationArrow,
  Package: Ph.Package,
  Palette: Ph.Palette,
  PanelLeft: Ph.SidebarSimple,
  PanelLeftClose: Ph.SidebarSimple,
  PanelLeftOpen: Ph.Sidebar,
  Paperclip: Ph.Paperclip,
  Pause: Ph.Pause,
  PauseCircle: Ph.PauseCircle,
  Pencil: Ph.Pencil,
  Phone: Ph.Phone,
  PictureInPicture2: Ph.PictureInPicture,
  Pin: Ph.PushPin,
  PinOff: Ph.PushPinSlash,
  Plane: Ph.Airplane,
  Play: Ph.Play,
  PlayCircle: Ph.PlayCircle,
  PlaySquare: Ph.PlayCircle,
  Plug: Ph.Plug,
  Plus: Ph.Plus,
  PlusCircle: Ph.PlusCircle,
  QrCode: Ph.QrCode,
  Radio: Ph.Radio,
  RectangleHorizontal: Ph.Rectangle,
  RefreshCw: Ph.ArrowsClockwise,
  Repeat: Ph.Repeat,
  Repeat2: Ph.Repeat,
  Reply: Ph.ArrowBendUpLeft,
  Rocket: Ph.Rocket,
  RotateCcw: Ph.ArrowCounterClockwise,
  RotateCw: Ph.ArrowClockwise,
  Route: Ph.Path,
  Ruler: Ph.Ruler,
  Save: Ph.FloppyDisk,
  ScanLine: Ph.Scan,
  Scissors: Ph.Scissors,
  Search: Ph.MagnifyingGlass,
  Send: Ph.PaperPlaneTilt,
  SendHorizonal: Ph.PaperPlaneRight,
  Settings: Ph.Gear,
  Settings2: Ph.GearSix,
  Share2: Ph.ShareNetwork,
  Shield: Ph.Shield,
  ShieldAlert: Ph.ShieldWarning,
  ShieldCheck: Ph.ShieldCheck,
  ShieldOff: Ph.ShieldSlash,
  ShoppingBag: Ph.ShoppingBag,
  ShoppingCart: Ph.ShoppingCart,
  Sliders: Ph.Sliders,
  SlidersHorizontal: Ph.SlidersHorizontal,
  Smartphone: Ph.DeviceMobile,
  Smile: Ph.Smiley,
  Sparkles: Ph.Sparkle,
  Square: Ph.Square,
  SquarePen: Ph.PencilSimpleLine,
  Star: Ph.Star,
  StarOff: Ph.Star,
  StopCircle: Ph.StopCircle,
  Store: Ph.Storefront,
  Subtitles: Ph.Subtitles,
  Sun: Ph.Sun,
  Tablet: Ph.DeviceTablet,
  Tag: Ph.Tag,
  Target: Ph.Target,
  Thermometer: Ph.Thermometer,
  ThumbsDown: Ph.ThumbsDown,
  ThumbsUp: Ph.ThumbsUp,
  Ticket: Ph.Ticket,
  Timer: Ph.Timer,
  Trash2: Ph.Trash,
  TrendingDown: Ph.TrendDown,
  TrendingUp: Ph.TrendUp,
  Trophy: Ph.Trophy,
  Truck: Ph.Truck,
  Tv: Ph.Television,
  Tv2: Ph.Television,
  Type: Ph.TextT,
  Upload: Ph.Upload,
  User: Ph.User,
  UserCheck: Ph.UserCheck,
  UserMinus: Ph.UserMinus,
  UserPlus: Ph.UserPlus,
  UserX: Ph.UserMinus,
  Users: Ph.Users,
  Users2: Ph.UsersThree,
  Video: Ph.Video,
  Volume1: Ph.SpeakerLow,
  Volume2: Ph.SpeakerHigh,
  VolumeX: Ph.SpeakerSlash,
  Vote: Ph.Checks,
  Wallet: Ph.Wallet,
  Wind: Ph.Wind,
  Wrench: Ph.Wrench,
  X: Ph.X,
  XCircle: Ph.XCircle,
  Zap: Ph.Lightning,
  ZoomIn: Ph.MagnifyingGlassPlus,
  ZoomOut: Ph.MagnifyingGlassMinus,
};

export const Activity = wrap(P.Activity);
export const AlertCircle = wrap(P.AlertCircle);
export const AlertTriangle = wrap(P.AlertTriangle);
export const AlignJustify = wrap(P.AlignJustify);
export const AlignLeft = wrap(P.AlignLeft);
export const AlignCenter = wrap(P.AlignCenter);
export const AlignRight = wrap(P.AlignRight);
export const Archive = wrap(P.Archive);
export const ArrowBigDown = wrap(P.ArrowBigDown);
export const ArrowBigUp = wrap(P.ArrowBigUp);
export const ArrowDown = wrap(P.ArrowDown);
export const ArrowLeft = wrap(P.ArrowLeft);
export const ArrowLeftRight = wrap(P.ArrowLeftRight);
export const ArrowRight = wrap(P.ArrowRight);
export const ArrowUp = wrap(P.ArrowUp);
export const ArrowUpDown = wrap(P.ArrowUpDown);
export const ArrowUpRight = wrap(P.ArrowUpRight);
export const AtSign = wrap(P.AtSign);
export const Award = wrap(P.Award);
export const BadgeCheck = wrap(P.BadgeCheck);
export const Ban = wrap(P.Ban);
export const BarChart2 = wrap(P.BarChart2);
export const BarChart3 = wrap(P.BarChart3);
export const Bell = wrap(P.Bell);
export const BellOff = wrap(P.BellOff);
export const BookOpen = wrap(P.BookOpen);
export const Bookmark = wrap(P.Bookmark);
export const BookmarkCheck = wrap(P.BookmarkCheck);
export const BookmarkPlus = wrap(P.BookmarkPlus);
export const Building2 = wrap(P.Building2);
export const Calendar = wrap(P.Calendar);
export const CalendarCheck = wrap(P.CalendarCheck);
export const CalendarClock = wrap(P.CalendarClock);
export const CalendarDays = wrap(P.CalendarDays);
export const Camera = wrap(P.Camera);
export const Captions = wrap(P.Captions);
export const CaptionsOff = wrap(P.CaptionsOff);
export const Car = wrap(P.Car);
export const Check = wrap(P.Check);
export const CheckCheck = wrap(P.CheckCheck);
export const CheckCircle = wrap(P.CheckCircle);
export const CheckCircle2 = wrap(P.CheckCircle2);
export const CheckSquare = wrap(P.CheckSquare);
export const ChevronDown = wrap(P.ChevronDown);
export const ChevronLeft = wrap(P.ChevronLeft);
export const ChevronRight = wrap(P.ChevronRight);
export const ChevronUp = wrap(P.ChevronUp);
export const Circle = wrap(P.Circle);
export const CircleDot = wrap(P.CircleDot);
export const ClipboardList = wrap(P.ClipboardList);
export const Clock = wrap(P.Clock);
export const Clock4 = wrap(P.Clock4);
export const Cloud = wrap(P.Cloud);
export const Code = wrap(P.Code);
export const Code2 = wrap(P.Code2);
export const Coffee = wrap(P.Coffee);
export const Compass = wrap(P.Compass);
export const Contrast = wrap(P.Contrast);
export const Copy = wrap(P.Copy);
export const CornerDownRight = wrap(P.CornerDownRight);
export const CreditCard = wrap(P.CreditCard);
export const Crop = wrap(P.Crop);
export const Crosshair = wrap(P.Crosshair);
export const Crown = wrap(P.Crown);
export const DollarSign = wrap(P.DollarSign);
export const Download = wrap(P.Download);
export const Droplet = wrap(P.Droplet);
export const Dumbbell = wrap(P.Dumbbell);
export const Edit = wrap(P.Edit);
export const Edit2 = wrap(P.Edit2);
export const ExternalLink = wrap(P.ExternalLink);
export const Eye = wrap(P.Eye);
export const EyeOff = wrap(P.EyeOff);
export const FileCheck = wrap(P.FileCheck);
export const FileEdit = wrap(P.FileEdit);
export const FileSpreadsheet = wrap(P.FileSpreadsheet);
export const FileText = wrap(P.FileText);
export const FileVideo = wrap(P.FileVideo);
export const Film = wrap(P.Film);
export const Flag = wrap(P.Flag);
export const Flame = wrap(P.Flame);
export const Folder = wrap(P.Folder);
export const FolderInput = wrap(P.FolderInput);
export const FolderOpen = wrap(P.FolderOpen);
export const Gauge = wrap(P.Gauge);
export const Gem = wrap(P.Gem);
export const Gift = wrap(P.Gift);
export const GitCompare = wrap(P.GitCompare);
export const Globe = wrap(P.Globe);
export const Globe2 = wrap(P.Globe2);
export const Grid3X3 = wrap(P.Grid3X3);
export const Grid3x3 = wrap(P.Grid3x3);
export const GripVertical = wrap(P.GripVertical);
export const Handshake = wrap(P.Handshake);
export const Hash = wrap(P.Hash);
export const Heart = wrap(P.Heart);
export const HelpCircle = wrap(P.HelpCircle);
export const History = wrap(P.History);
export const Home = wrap(P.Home);
export const Hourglass = wrap(P.Hourglass);
export const Image = wrap(P.Image);
export const ImageIcon = wrap(P.ImageIcon);
export const ImagePlus = wrap(P.ImagePlus);
export const Images = wrap(P.Images);
export const Info = wrap(P.Info);
export const Keyboard = wrap(P.Keyboard);
export const Landmark = wrap(P.Landmark);
export const Layers = wrap(P.Layers);
export const LayoutDashboard = wrap(P.LayoutDashboard);
export const LayoutGrid = wrap(P.LayoutGrid);
export const LayoutTemplate = wrap(P.LayoutTemplate);
export const LibraryBig = wrap(P.LibraryBig);
export const LifeBuoy = wrap(P.LifeBuoy);
export const Link = wrap(P.Link);
export const Link2 = wrap(P.Link2);
export const List = wrap(P.List);
export const ListFilter = wrap(P.ListFilter);
export const ListVideo = wrap(P.ListVideo);
export const Loader2 = wrap(P.Loader2);
export const Lock = wrap(P.Lock);
export const LogOut = wrap(P.LogOut);
export const Mail = wrap(P.Mail);
export const Map = wrap(P.Map);
export const MapPin = wrap(P.MapPin);
export const Maximize = wrap(P.Maximize);
export const Maximize2 = wrap(P.Maximize2);
export const Medal = wrap(P.Medal);
export const Megaphone = wrap(P.Megaphone);
export const Menu = wrap(P.Menu);
export const MessageCircle = wrap(P.MessageCircle);
export const MessageSquare = wrap(P.MessageSquare);
export const Mic = wrap(P.Mic);
export const Minimize = wrap(P.Minimize);
export const Minus = wrap(P.Minus);
export const Monitor = wrap(P.Monitor);
export const MonitorPlay = wrap(P.MonitorPlay);
export const Moon = wrap(P.Moon);
export const MoreHorizontal = wrap(P.MoreHorizontal);
export const MoreVertical = wrap(P.MoreVertical);
export const Mountain = wrap(P.Mountain);
export const Music2 = wrap(P.Music2);
export const Navigation = wrap(P.Navigation);
export const Package = wrap(P.Package);
export const Palette = wrap(P.Palette);
export const PanelLeft = wrap(P.PanelLeft);
export const PanelLeftClose = wrap(P.PanelLeftClose);
export const PanelLeftOpen = wrap(P.PanelLeftOpen);
export const Paperclip = wrap(P.Paperclip);
export const Pause = wrap(P.Pause);
export const PauseCircle = wrap(P.PauseCircle);
export const Pencil = wrap(P.Pencil);
export const Phone = wrap(P.Phone);
export const PictureInPicture2 = wrap(P.PictureInPicture2);
export const Pin = wrap(P.Pin);
export const PinOff = wrap(P.PinOff);
export const Plane = wrap(P.Plane);
export const Play = wrap(P.Play);
export const PlayCircle = wrap(P.PlayCircle);
export const PlaySquare = wrap(P.PlaySquare);
export const Plug = wrap(P.Plug);
export const Plus = wrap(P.Plus);
export const PlusCircle = wrap(P.PlusCircle);
export const QrCode = wrap(P.QrCode);
export const Radio = wrap(P.Radio);
export const RectangleHorizontal = wrap(P.RectangleHorizontal);
export const RefreshCw = wrap(P.RefreshCw);
export const Repeat = wrap(P.Repeat);
export const Repeat2 = wrap(P.Repeat2);
export const Reply = wrap(P.Reply);
export const Rocket = wrap(P.Rocket);
export const RotateCcw = wrap(P.RotateCcw);
export const RotateCw = wrap(P.RotateCw);
export const Route = wrap(P.Route);
export const Ruler = wrap(P.Ruler);
export const Save = wrap(P.Save);
export const ScanLine = wrap(P.ScanLine);
export const Scissors = wrap(P.Scissors);
export const Search = wrap(P.Search);
export const Send = wrap(P.Send);
export const SendHorizonal = wrap(P.SendHorizonal);
export const Settings = wrap(P.Settings);
export const Settings2 = wrap(P.Settings2);
export const Share2 = wrap(P.Share2);
export const Shield = wrap(P.Shield);
export const ShieldAlert = wrap(P.ShieldAlert);
export const ShieldCheck = wrap(P.ShieldCheck);
export const ShieldOff = wrap(P.ShieldOff);
export const ShoppingBag = wrap(P.ShoppingBag);
export const ShoppingCart = wrap(P.ShoppingCart);
export const Sliders = wrap(P.Sliders);
export const SlidersHorizontal = wrap(P.SlidersHorizontal);
export const Smartphone = wrap(P.Smartphone);
export const Smile = wrap(P.Smile);
export const Sparkles = wrap(P.Sparkles);
export const Square = wrap(P.Square);
export const SquarePen = wrap(P.SquarePen);
export const Star = wrap(P.Star);
export const StarOff = wrap(P.StarOff);
export const StopCircle = wrap(P.StopCircle);
export const Store = wrap(P.Store);
export const Subtitles = wrap(P.Subtitles);
export const Sun = wrap(P.Sun);
export const Tablet = wrap(P.Tablet);
export const Tag = wrap(P.Tag);
export const Target = wrap(P.Target);
export const Thermometer = wrap(P.Thermometer);
export const ThumbsDown = wrap(P.ThumbsDown);
export const ThumbsUp = wrap(P.ThumbsUp);
export const Ticket = wrap(P.Ticket);
export const Timer = wrap(P.Timer);
export const Trash2 = wrap(P.Trash2);
export const TrendingDown = wrap(P.TrendingDown);
export const TrendingUp = wrap(P.TrendingUp);
export const Trophy = wrap(P.Trophy);
export const Truck = wrap(P.Truck);
export const Tv = wrap(P.Tv);
export const Tv2 = wrap(P.Tv2);
export const Type = wrap(P.Type);
export const Upload = wrap(P.Upload);
export const User = wrap(P.User);
export const UserCheck = wrap(P.UserCheck);
export const UserMinus = wrap(P.UserMinus);
export const UserPlus = wrap(P.UserPlus);
export const UserX = wrap(P.UserX);
export const Users = wrap(P.Users);
export const Users2 = wrap(P.Users2);
export const Video = wrap(P.Video);
export const Volume1 = wrap(P.Volume1);
export const Volume2 = wrap(P.Volume2);
export const VolumeX = wrap(P.VolumeX);
export const Vote = wrap(P.Vote);
export const Wallet = wrap(P.Wallet);
export const Wind = wrap(P.Wind);
export const Wrench = wrap(P.Wrench);
export const X = wrap(P.X);
export const XCircle = wrap(P.XCircle);
export const Zap = wrap(P.Zap);
export const ZoomIn = wrap(P.ZoomIn);
export const ZoomOut = wrap(P.ZoomOut);
